import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, LogIn, Mail } from 'lucide-react';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import AuthLayout from '../components/AuthLayout';
import ErrorBanner from '../components/ErrorBanner';

export default function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/login', { email, password });
      login(data.token, data.user, remember);
      // Admins land directly in the admin console; regular users go to the
      // dashboard. Either way they can still navigate freely once signed in.
      navigate(data.user?.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your RankOnAI workspace."
      // Self-serve signup is hidden for now — accounts are provisioned via
      // billing/admin. Restore the footer below to re-enable the "Create one" link.
      // footer={
      //   <>
      //     Don&apos;t have an account?{' '}
      //     <Link to="/register" className="font-semibold text-brand-600 hover:underline">
      //       Create one
      //     </Link>
      //   </>
      // }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@company.com"
            />
          </div>
        </div>
        <div>
          <label className="label">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-ink-800 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500/30"
            />
            Remember me
          </label>
          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-brand-600 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        {error && <ErrorBanner body={error} onDismiss={() => setError('')} />}
        <button className="btn-primary w-full" disabled={loading}>
          {loading ? (
            'Signing in…'
          ) : (
            <>
              <LogIn className="w-4 h-4" /> Sign in
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
